import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import {
  HttpHeaders,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { environment } from '../environments/environment';
import { CustomRouteReuseStrategy } from './core/route-reuse-strategy';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import localeFr from '@angular/common/locales/fr';

registerLocaleData(localeFr);

function jwtOptionsFactory(platformId): any {
  return {
    tokenGetter: () => {
      let token = null;
      if (isPlatformBrowser(platformId)) {
        token = sessionStorage.getItem('token');
      }
      return token;
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({ includePostRequests: true }),
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [PLATFORM_ID],
        },
      }),
    ),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);
      const platformId = inject(PLATFORM_ID);

      const basic = new SetContextLink((prevContext, operations) => ({
        headers: new HttpHeaders({
          Accept: 'application/json, text/plain, */*',
        }),
      }));

      const auth = new SetContextLink(() => {
        let headers = new HttpHeaders();
        const token = isPlatformBrowser(platformId) ? localStorage.getItem('token') : null;

        if (token) {
          headers = headers.append('Authorization', `Bearer ${token}`);
        }
        return {
          headers,
        };
      });

      const defaultOptions: ApolloClient.DefaultOptions = {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      };

      const removeTypeNameLink = new RemoveTypenameFromVariablesLink();

      return {
        default: {
          link: ApolloLink.from([
            removeTypeNameLink,
            basic,
            auth,
            httpLink.create({ uri: environment.baseUrl + 'graphql/' }),
          ]),
          cache: new InMemoryCache({
            resultCaching: false,
          }),
          defaultOptions,
          ssrMode: true,
        },
      };
    }),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false || 'none',
        },
      },
    }),
    { provide: LOCALE_ID, useValue: 'fr-FR' },
  ],
};
